import { CalendarClockIcon, CalendarOffIcon, RadioIcon } from 'lucide-react'
import type { ComponentProps, ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { TradeFairState } from '@/api/tradeFairs'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { STATE_HINT_KEYS, STATE_LABEL_KEYS } from '@/components/tradefairs/stateLabels'

type BadgeVariant = ComponentProps<typeof Badge>['variant']

/**
 * `RUNNING` is the loud one here, the other way round from the user list. A fair that is on right
 * now is the row somebody at a stand is looking for, and most rows in a grown list are past.
 */
const APPEARANCE: Record<
  TradeFairState,
  { variant: BadgeVariant; icon: ComponentType<{ className?: string }> }
> = {
  [TradeFairState.RUNNING]: { variant: 'default', icon: RadioIcon },
  [TradeFairState.UPCOMING]: { variant: 'outline', icon: CalendarClockIcon },
  [TradeFairState.PAST]: { variant: 'ghost', icon: CalendarOffIcon },
}

/**
 * The state is computed by the backend from the period in the business time zone. Showing it
 * rather than deriving it here keeps the badge and the filter from disagreeing across midnight.
 */
export function TradeFairStateBadge({ state }: { state: TradeFairState }) {
  const { t } = useTranslation()
  const { variant, icon: Icon } = APPEARANCE[state]

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={variant} className="gap-1">
          <Icon aria-hidden />
          {t(STATE_LABEL_KEYS[state])}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{t(STATE_HINT_KEYS[state])}</TooltipContent>
    </Tooltip>
  )
}
